import React, { useCallback, useMemo, useState } from 'react';
import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useNavigation } from '@navigation';
import { PopupMenuItem, PopupMenuRef } from '@bcp-nextgen-dx-component-factory/accolade-design-system';
import { useBuildPhoneCodes } from '@hooks';
import { ActiveItem } from '@bcp-nextgen-dx-component-factory/accolade-design-system/dist/molecules/InputSelect/InputSelect.models';

interface RequestToCallOpenResources {
	title: string;
	subtitle: string;
	inputLabel: string;
	inputCounty: string;
	linkBack: string;
	buttonCall: string;
}

interface RequestToCallOpenOutput {
	resources: RequestToCallOpenResources;
	buildDialingPhoneCodes: PopupMenuItem[];
	isDisable: boolean;
	popupMenuRef: React.RefObject<PopupMenuRef>;
	onChangeValue: (value: string, indicative: string) => void;
	onClearValue: () => void;
	handlePress: (item: ActiveItem | undefined) => void;
	goTo: () => void;
}

interface RequestToCallOpenHelperProps {
	setPhone: (phone: string) => void;
	setIndicative: React.Dispatch<React.SetStateAction<string>>;
	setContactToCallCountry: (country: string) => void;
}

export const useRequestToCallOpenHelper = (props: RequestToCallOpenHelperProps): RequestToCallOpenOutput => {
	const { setPhone, setIndicative, setContactToCallCountry } = props;
	const t = useTranslation();
	const { back } = useNavigation();
	const [isDisable, setIsDisable] = useState<boolean>(true);
	const { phoneCodes, initialPhoneId, getSelectedPhone } = useBuildPhoneCodes({});
	const popupMenuRef = React.useRef<PopupMenuRef>(null);

	useDidMount(() => {
		popupMenuRef.current?.setActiveItem(initialPhoneId);
		if (initialPhoneId !== undefined) {
			const selectedPhone = getSelectedPhone(phoneCodes[initialPhoneId].content as string);
			setIndicative(selectedPhone?.phonePrefix as string);
			setContactToCallCountry(selectedPhone?.code as string);
		}
	});

	const handlePress = (item: ActiveItem | undefined) => {
		if (item?.id !== undefined) {
			const selectedPhone = getSelectedPhone(item.content);
			setIndicative(selectedPhone?.phonePrefix as string);
			setContactToCallCountry(selectedPhone?.code as string);
		}
		popupMenuRef.current?.hide();
	};

	const resources = useMemo((): RequestToCallOpenResources => {
		return {
			title: t('placeholders.requestToCall.open.title'),
			subtitle: t('placeholders.requestToCall.open.subtitle'),
			inputLabel: t('placeholders.requestToCall.form.phone'),
			inputCounty: t('placeholders.requestToCall.form.country'),
			linkBack: t('general.back'),
			buttonCall: t('placeholders.requestToCall.open.askToCall'),
		};
	}, [t]);

	const onChangeValue = (value: string, indicative: string) => {
		if (value !== '' && ((indicative === '+351' && value.length === 9) || indicative !== '+351')) {
			setIsDisable(false);
			setPhone(value);
		} else {
			setIsDisable(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	};

	const onClearValue = useCallback(() => {
		setIsDisable(true);
		setPhone('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const goTo = useCallback(() => {
		back();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		resources,
		isDisable,
		buildDialingPhoneCodes: phoneCodes,
		popupMenuRef,
		handlePress,
		onChangeValue,
		onClearValue,
		goTo,
	};
};
